/**
 * avatars/speech-bubble.js
 * Quản lý bóng thoại 2D Sprite (Speech Bubble) bám đầu nhân vật Voxel
 */

import * as THREE from 'three';
import { CANVAS_FONTS } from '../core/fonts.js';

/**
 * Tạo bóng thoại (Speech Bubble Sprite) lơ lửng bám đầu nhân vật
 * @param {object} avatar
 */
export function createSpeechBubble(avatar) {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 256;
  avatar.speechCanvas = canvas;
  avatar.speechCtx = canvas.getContext('2d');

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  avatar.speechTexture = texture;
  avatar.disposables.push(texture);

  const spriteMat = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: false
  });
  avatar.disposables.push(spriteMat);

  avatar.speechSprite = new THREE.Sprite(spriteMat);
  avatar.speechSprite.scale.set(1.7, 0.85, 1.0);
  avatar.speechSprite.position.set(0, 2.05, 0);
  avatar.speechSprite.visible = false;
  avatar.root.add(avatar.speechSprite);
}

/**
 * Hiển thị lời thoại trong bóng thoại
 * @param {object} avatar
 * @param {string} text
 * @param {number} [durationMs=5000]
 */
export function speak(avatar, text, durationMs = 5000) {
  if (!avatar.speechCtx || !avatar.speechCanvas) return;
  const ctx = avatar.speechCtx;
  const w = avatar.speechCanvas.width;
  const h = avatar.speechCanvas.height;

  ctx.clearRect(0, 0, w, h);

  const pad = 20;
  const r = 24;
  const boxW = w - pad * 2;
  const boxH = h - pad * 2 - 40;

  // Nền trắng bóng đổ
  ctx.shadowColor = 'rgba(0, 0, 0, 0.25)';
  ctx.shadowBlur = 16;
  ctx.shadowOffsetY = 6;

  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.roundRect(pad, pad, boxW, boxH, r);
  ctx.fill();

  // Đuôi bóng nói
  ctx.beginPath();
  ctx.moveTo(w / 2 - 20, pad + boxH);
  ctx.lineTo(w / 2, pad + boxH + 32);
  ctx.lineTo(w / 2 + 20, pad + boxH);
  ctx.closePath();
  ctx.fill();

  ctx.shadowColor = 'transparent';
  ctx.strokeStyle = '#38bdf8';
  ctx.lineWidth = 4;
  ctx.stroke();

  ctx.fillStyle = '#0f172a';
  ctx.font = `bold 26px ${CANVAS_FONTS.sans}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const words = text.split(' ');
  const lines = [];
  let curLine = '';
  const maxLineW = boxW - 50;

  for (const word of words) {
    const testLine = curLine ? `${curLine} ${word}` : word;
    const metrics = ctx.measureText(testLine);
    if (metrics.width > maxLineW) {
      if (curLine) lines.push(curLine);
      curLine = word;
    } else {
      curLine = testLine;
    }
  }
  if (curLine) lines.push(curLine);

  const lineH = 34;
  const startY = pad + boxH / 2 - ((lines.length - 1) * lineH) / 2;
  lines.forEach((line, idx) => {
    ctx.fillText(line, w / 2, startY + idx * lineH);
  });

  avatar.speechTexture.needsUpdate = true;
  avatar.speechSprite.visible = true;

  if (avatar.speechTimeout) clearTimeout(avatar.speechTimeout);
  if (durationMs > 0) {
    avatar.speechTimeout = setTimeout(() => {
      avatar.speechSprite.visible = false;
    }, durationMs);
  }
}

/**
 * Ẩn bóng thoại ngay lập tức
 * @param {object} avatar
 */
export function hideSpeech(avatar) {
  if (avatar.speechSprite) avatar.speechSprite.visible = false;
  if (avatar.speechTimeout) clearTimeout(avatar.speechTimeout);
}
